// .pen: ZeLvp (ds/selfie/balance-row--default) / v0HBR (--alterada)
import { ArrowCounterClockwise, ArrowDown, ArrowUp } from '@phosphor-icons/react';

import { NumericInput } from '../../common/NumericInput';
import pt from '../../../locales/pt';
import { cn } from '../../../utils/cn';
import { formatEUR } from '../../../utils/money';

function format(template, params) {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    params[key] === undefined ? `{${key}}` : String(params[key]),
  );
}

/**
 * Linha de saldo editável — pré-preenchida com reset "Igual ao mês anterior".
 * .pen: ZeLvp (ds/selfie/balance-row--default) / v0HBR (--alterada).
 *
 * @param {'sage'|'warning'} [tone] Tom do chip do ícone — sage para ativos,
 *   warning para dívidas (independente de `changed`).
 * @param {boolean} [favorable] Quando `changed`, se a variação é boa para o
 *   património líquido (ativo subiu / dívida desceu) — controla o tom do
 *   badge de delta.
 */
export function SelfieBalanceRow({
  icon: Icon,
  tone = 'sage',
  name,
  beforeAmount,
  value,
  onChange,
  onReset,
  delta,
  favorable = true,
  currencyPrefix = '€',
  currencyLabel,
  isCrypto = false,
  pricePerUnit,
  onPriceChange,
  ariaLabel,
}) {
  const changed = delta !== null && delta !== 0;
  const copy = pt.snapshots.ritual.balances;
  const chipToneClass =
    tone === 'warning' ? 'bg-warning-tint text-warning-fg' : 'bg-sage-tint text-sage-on-tint';

  return (
    <div
      data-ds-id={changed ? 'ds/selfie/balance-row--alterada' : 'ds/selfie/balance-row--default'}
      className={cn(
        'flex flex-col gap-space-2 rounded-ds-lg border-[1.5px] bg-card px-space-3 py-space-2-5',
        changed ? 'border-sage-400' : 'border-border',
      )}
    >
      <div className="flex items-center gap-space-2-5">
        <span
          className={cn(
            'flex size-[34px] shrink-0 items-center justify-center rounded-ds-sm',
            chipToneClass,
          )}
        >
          <Icon className="size-[18px]" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="type-body truncate font-semibold text-card-foreground">{name}</p>
          {beforeAmount != null ? (
            <p className="type-chip mt-0.5 truncate text-muted-foreground">
              {format(copy.beforeLabel, { amount: formatEUR(beforeAmount) })}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-space-1">
          <span className="font-display text-type-title-card font-bold text-muted-foreground">
            {currencyPrefix}
          </span>
          <NumericInput
            className={cn(
              // .pen `row-amount` is fit_content (no fixed width) — size generously so
              // large debt/asset balances (6+ digits) don't clip (regression found in QA).
              'w-28 min-w-0 border-0 bg-transparent px-0 py-0 text-right font-display text-type-display-stat font-bold shadow-none focus-visible:ring-0',
              changed ? 'text-sage-on-tint' : 'text-card-foreground',
            )}
            placeholder="0,00"
            value={value}
            onChange={onChange}
            aria-label={ariaLabel}
          />
        </div>
      </div>

      {currencyLabel ? (
        <p className="type-chip -mt-1 text-right uppercase tracking-wide text-muted-foreground">
          {currencyLabel}
        </p>
      ) : null}

      {isCrypto ? (
        <div className="flex justify-end">
          <NumericInput
            className="w-[7.5rem] border-0 border-b border-input-border bg-transparent px-0 py-0 pb-space-1 text-right font-mono text-type-chip text-muted-foreground shadow-none focus-visible:ring-0"
            placeholder="EUR/un"
            value={pricePerUnit ?? ''}
            onChange={onPriceChange}
            aria-label={`${name} ${pt.snapshots.new.pricePerUnitLabel}`}
          />
        </div>
      ) : null}

      {changed ? (
        <div className="flex items-center justify-end gap-space-2">
          <span
            className={cn(
              'inline-flex items-center gap-space-1 rounded-ds-full px-space-2-5 py-space-1 text-type-chip font-bold font-display',
              favorable ? 'bg-sage-tint text-sage-on-tint' : 'bg-warning-tint text-warning-fg',
            )}
          >
            {delta > 0 ? (
              <ArrowUp className="size-3 shrink-0" weight="bold" aria-hidden />
            ) : (
              <ArrowDown className="size-3 shrink-0" weight="bold" aria-hidden />
            )}
            {delta >= 0 ? '+' : ''}
            {formatEUR(delta)}
          </span>
          {onReset ? (
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-space-1 rounded-ds-sm bg-muted px-space-2-5 py-space-1 text-type-chip font-semibold text-muted-foreground transition hover:text-foreground"
            >
              <ArrowCounterClockwise className="size-3 shrink-0" weight="regular" aria-hidden />
              {copy.resetPrevious}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default SelfieBalanceRow;
